"use client";

import Link from "next/link";
import { useEffect, useRef, useState } from "react";

import { useOptionalTranslations } from "@/i18n";

export const Footer = () => {
  // Optional: the footer also renders on legacy non-locale routes with no
  // LocaleProvider, where we fall back to English
  const dictionary = useOptionalTranslations();
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [shouldLoadVideo, setShouldLoadVideo] = useState(false);

  useEffect(() => {
    if (!containerRef.current || shouldLoadVideo) return;

    // Attach the observer only after window load + idle. On pages whose content
    // arrives client-side the footer sits in the viewport before data lands, and
    // observing immediately made every page stream the background video during
    // initial page load.
    let observer: IntersectionObserver | null = null;
    let idleId: number | undefined;
    const attach = () => {
      if (!containerRef.current) return;
      observer = new IntersectionObserver(
        (entries) => {
          if (entries.some((entry) => entry.isIntersecting)) {
            setShouldLoadVideo(true);
            observer?.disconnect();
          }
        },
        { rootMargin: "200px" }
      );
      observer.observe(containerRef.current);
    };
    const schedule = () => {
      if (typeof window.requestIdleCallback === "function") {
        idleId = window.requestIdleCallback(attach, { timeout: 4000 });
      } else {
        idleId = window.setTimeout(attach, 2500) as unknown as number;
      }
    };
    if (document.readyState === "complete") {
      schedule();
    } else {
      window.addEventListener("load", schedule, { once: true });
    }
    return () => {
      window.removeEventListener("load", schedule);
      if (idleId !== undefined && typeof window.cancelIdleCallback === "function") window.cancelIdleCallback(idleId);
      observer?.disconnect();
    };
  }, [shouldLoadVideo]);

  return (
    <footer className="bg-white dark:bg-black text-black dark:text-white py-0 px-0 relative overflow-hidden transition-colors min-h-[60vh] md:min-h-[80vh] flex items-center justify-center">
      {/* Video Background */}
      <div ref={containerRef} className="absolute inset-0 z-0 overflow-hidden">
        {shouldLoadVideo ? (
          <video
            src="/videos/footer-bg.mp4"
            autoPlay
            muted
            loop
            playsInline
            className="absolute inset-0 h-full w-full object-cover"
          />
        ) : (
          <div className="absolute inset-0 bg-black/50 animate-pulse" />
        )}
      </div>

      {/* Footer bar */}
      <div className="absolute bottom-0 left-0 right-0 z-10 bg-black/60 backdrop-blur-sm px-6 py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between gap-4 text-sm text-white/70">
          <p>&copy; {new Date().getFullYear()} Youth Alive Fellowship Canada</p>
          <div className="flex items-center gap-4 sm:gap-6">
            <Link
              href="/faq"
              className="hover:text-white transition-colors underline underline-offset-2 decoration-white/30 hover:decoration-white/60"
            >
              FAQ
            </Link>
            <Link
              href="/privacypolicy"
              className="hover:text-white transition-colors underline underline-offset-2 decoration-white/30 hover:decoration-white/60"
            >
              {dictionary?.footer.privacyPolicy ?? "Privacy Policy"}
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
};
